import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Table, TableHeader, TableBody, TableCell, TableHead, TableRow } from '../components/ui/table';
import { Pencil, Trash2, Play, Pause, X, Plus, Copy } from 'lucide-react';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const RuleManager = () => {
  const [accounts, setAccounts] = useState([]);
  // Auth helper
  const getAuthHeaders = () => {
    const token = localStorage.getItem('gads_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': 'Bearer ' + token } : {}),
    };
  };

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    action: '',
    account_id: '',
    status: 'enable',
    budget_update_type: 'Tăng',
    budget_update_value: 0,
    budget_update_unit: '%',
    metric_time: '3d',
    conditions: [{ metric_type: '', comparison_operator: '', value: '', logical_operator: 'and' }]
  });

  // Các options cho form
  const metricTypes = [
    { value: 'conversion', label: 'Chuyển đổi' },
    { value: 'ctr', label: 'CTR' },
    { value: 'cost', label: 'Chi phí' },
    { value: 'cpa', label: 'CPA' },
    { value: 'cpc', label: 'CPC' },
    { value: 'cpm', label: 'CPM' },
    { value: 'impression', label: 'Hiển thị' },
    { value: 'clicks', label: 'Click' },
    { value: 'conversion_rate', label: 'Tỷ lệ chuyển đổi' },
    { value: 'cost_after_conv', label: 'Chi phí phát sinh từ đơn sau cùng' },
    { value: 'time', label: 'Thời gian' },
    { value: 'custom', label: 'Công thức tùy chỉnh' },
  ];

  // Các options cho form
  const metricTypeForActiveCampaign = [
    { value: 'conversion', label: 'Chuyển đổi' },
    { value: 'cost', label: 'Chi phí' },
    { value: 'cpa', label: 'CPA' },
    { value: 'time', label: 'Thời gian' },
  ];

  const comparisonOperators = [
    { value: 'greater_than', label: 'lớn hơn' },
    { value: 'less_than', label: 'nhỏ hơn' },
    { value: 'equals', label: 'bằng' },
    { value: 'less_than_or_equals', label: 'nhỏ hơn hoặc bằng' },
    { value: 'greater_than_or_equals', label: 'lớn hơn hoặc bằng' },
    { value: 'less_than_or_equals_percent_budget', label: 'nhỏ hơn hoặc bằng phần trăm budget' },
    { value: 'greater_than_or_equals_percent_budget', label: 'lớn hơn hoặc bằng phần trăm budget' },
    { value: 'not_equals', label: 'không bằng' }
  ];

  const logicalOperators = [
    { value: 'and', label: 'Và' },
    { value: 'or', label: 'Hoặc' }
  ];

  // Lấy danh sách tài khoản
  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/accounts?limit=100`);
      setAccounts(response.data.accounts);
    } catch (err) {
      setError('Lỗi khi lấy danh sách tài khoản');
      console.error(err);
    }
  };

  // Lấy danh sách rules
  const fetchRules = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/rules`);
      setRules(response.data);
    } catch (err) {
      setError('Lỗi khi lấy danh sách rules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (accounts.length > 0) {
      fetchRules();
    }
  }, [accounts]);

  // Xử lý thay đổi form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleConditionChange = (index, field, value) => {
    const newConditions = [...formData.conditions];
    newConditions[index] = {
      ...newConditions[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      conditions: newConditions
    }));
  };

  // Thêm điều kiện mới
  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        { metric_type: '', comparison_operator: '', value: '', logical_operator: 'and' }
      ]
    }));
  };

  // Xóa điều kiện
  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      action: '',
      account_id: '',
      status: 'enable',
      budget_update_type: 'Tăng',
      budget_update_value: 0,
      budget_update_unit: '%',
      metric_time: '3d',
      conditions: [{ metric_type: '', comparison_operator: '', value: '', logical_operator: 'and' }]
    });
    setShowAddForm(false);
    setEditingRule(null);
  };

  // Xử lý submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      if (editingRule) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/rules/${editingRule.id}`, formData);
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/rules`, formData);
      }

      resetForm();
      fetchRules();
    } catch (err) {
      setError(err.response?.data?.error || 'Lỗi khi lưu rule');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý cập nhật trạng thái rule
  const handleStatusChange = async (ruleId, rule) => {
    try {
      setLoading(true);
      setError(null);
      const newStatus = rule.status === 'enable' ? 'disable' : 'enable';
      const data = {
        name: rule.name,
        account_id: rule.account_id,
        action: rule.action,
        status: newStatus,
        budget_update_type: rule.budget_update_type,
        budget_update_value: rule.budget_update_value,
        budget_update_unit: rule.budget_update_unit,
        metric_time: rule.metric_time,
        conditions: rule.rule_conditions
      };
      await axios.put(`${process.env.REACT_APP_API_URL}/api/rules/${ruleId}`, data);
      fetchRules();
    } catch (err) {
      setError(err.response?.data?.error || 'Lỗi khi cập nhật trạng thái');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý xóa rule
  const handleDelete = async (ruleId) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa rule này?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/rules/${ruleId}`);
      fetchRules();
    } catch (err) {
      setError(err.response?.data?.error || 'Lỗi khi xóa rule');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý chỉnh sửa rule
  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      action: rule.action,
      account_id: rule.account_id,
      status: rule.status,
      budget_update_type: rule.budget_update_type,
      budget_update_value: rule.budget_update_value,
      budget_update_unit: rule.budget_update_unit,
      metric_time: rule.metric_time,
      conditions: rule.rule_conditions?.map(condition => ({
        ...condition,
        logical_operator: condition.logical_operator || 'and'
      }))
    });
    setShowAddForm(true);
  };

  // Xử lý copy rule
  const handleCopy = (rule) => {
    setEditingRule(null);
    setFormData({
      name: `${rule.name} (Copy)`,
      action: rule.action,
      account_id: '',
      status: 'enable',
      budget_update_type: rule.budget_update_type,
      budget_update_value: rule.budget_update_value,
      budget_update_unit: rule.budget_update_unit,
      metric_time: rule.metric_time,
      conditions: rule.rule_conditions?.map(condition => ({
        ...condition,
        logical_operator: condition.logical_operator || 'and'
      }))
    });
    setShowAddForm(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h2 className="text-md font-bold">Quản lý Rules</h2>
          <button
            onClick={() => setShowGuide(true)}
            className="text-blue-500 hover:text-blue-700 flex items-center gap-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Hướng dẫn sử dụng
          </button>
        </div>
        <div className="flex items-center gap-4">
          <Select
            value={selectedAccount}
            onValueChange={setSelectedAccount}
          >
            <SelectTrigger className="w-[200px] bg-white">
              <SelectValue placeholder="Lọc theo tài khoản" />
            </SelectTrigger>
            <SelectContent>
              {accounts?.map(account => (
                <SelectItem key={account.customer_id} value={`${account.customer_id}`}>
                  {account.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-2 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Thêm Rule mới
          </Button>
        </div>
      </div>

      {showGuide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Hướng dẫn sử dụng Rules</h2>
              <button
                onClick={() => setShowGuide(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">1. Các loại điều kiện</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <span className="font-medium">Điều kiện thông thường:</span> Chọn metric, toán tử so sánh và giá trị
                  </li>
                  <li>
                    <span className="font-medium">Điều kiện Thời gian:</span> Chọn khoảng thời gian trong ngày để rule hoạt động
                  </li>
                  <li>
                    <span className="font-medium">Điều kiện Custom:</span> Nhập công thức tùy chỉnh với các biến có sẵn
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">2. Các biến có thể sử dụng trong công thức Custom</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li><code>cost</code>: Chi phí</li>
                  <li><code>conversions</code>: Số chuyển đổi</li>
                  <li><code>impressions</code>: Số lượt hiển thị</li>
                  <li><code>clicks</code>: Số lượt click</li>
                  <li><code>ctr</code>: Tỷ lệ click</li>
                  <li><code>cpc</code>: Chi phí mỗi click</li>
                  <li><code>cpa</code>: Chi phí mỗi chuyển đổi</li>
                  <li><code>cpm</code>: Chi phí mỗi 1000 hiển thị</li>
                  <li><code>conversion_rate</code>: Tỷ lệ chuyển đổi</li>
                  <li><code>cost_after_conv</code>: Chi phí phát sinh từ đơn sau cùng (Cost.A.Conv)</li>
                  <li><code>budget</code>: Ngân sách</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">3. Các toán tử có thể sử dụng</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li><code>+</code>, <code>-</code>, <code>*</code>, <code>/</code>: Các phép toán cơ bản</li>
                  <li><code>&gt;</code>, <code>&lt;</code>, <code>==</code>, <code>!=</code>, <code>&gt;=</code>, <code>&lt;=</code>: Các toán tử so sánh</li>
                  <li><code>&&</code>, <code>||</code>: Các toán tử logic</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">4. Ví dụ về công thức Custom</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li><code>cost &gt; 500000</code>: Chi phí lớn hơn 500,000</li>
                  <li><code>(conversions * 2) + cpa</code>: Số chuyển đổi nhân 2 cộng với CPA</li>
                  <li><code>cost &gt; 500000 && ctr &lt; 0.05</code>: Chi phí lớn hơn 500,000 và CTR nhỏ hơn 5%</li>
                  <li><code>(cost / conversions) &gt; 100000 || ctr &lt; 0.02</code>: CPA lớn hơn 100,000 hoặc CTR nhỏ hơn 2%</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">5. Các hành động có thể thực hiện</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li><span className="font-medium">Pause Campaign:</span> Tạm dừng chiến dịch</li>
                  <li><span className="font-medium">Active Campaign:</span> Kích hoạt chiến dịch</li>
                  <li><span className="font-medium">Update Budget:</span> Cập nhật ngân sách (tăng/giảm theo % hoặc số tiền)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingRule ? 'Chỉnh sửa Rule' : 'Thêm Rule mới'}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={resetForm}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tên Rule</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Nhập tên rule"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tài khoản</Label>
                  <Select
                    value={formData.account_id}
                    onValueChange={(value) => handleInputChange({ target: { name: 'account_id', value: `${value}` } })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn tài khoản" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts?.map(account => (
                        <SelectItem key={account.customer_id} value={`${account.customer_id}`}>
                          {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Hành động</Label>
                <div className="grid grid-cols-2 gap-4">
                  <Select
                    value={formData.action}
                    onValueChange={(value) => {
                      value === 'Active Campaign' && handleInputChange({ target: { name: 'metric_time', value: '3d' } });
                      value !== 'Active Campaign' && handleInputChange({ target: { name: 'metric_time', value: null } });
                      handleInputChange({ target: { name: 'action', value } })
                    }
                  }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn hành động" />
                    </SelectTrigger>
                    <SelectContent>
                      {['Pause Campaign', 'Active Campaign', 'Update Budget'].map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {formData.action === 'Active Campaign' && (
                    <div className="flex gap-2">
                      <Select
                        value={formData.metric_time || '3d'}
                        onValueChange={(value) => handleInputChange({ target: { name: 'metric_time', value } })}
                      >
                        <SelectTrigger className="w-[250px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3d">Dựa trên dữ liệu 3 Ngày</SelectItem>
                          <SelectItem value="7d">Dựa trên dữ liệu 7 Ngày</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {formData.action === 'Update Budget' && (
                    <div className="flex gap-2">
                      <Select
                        value={formData.budget_update_type}
                        onValueChange={(value) => handleInputChange({ target: { name: 'budget_update_type', value } })}
                      >
                        <SelectTrigger className="w-[100px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Tăng">Tăng</SelectItem>
                          <SelectItem value="Giảm">Giảm</SelectItem>
                        </SelectContent>
                      </Select>

                      <Input
                        type="number"
                        name="budget_update_value"
                        value={formData.budget_update_value}
                        onChange={handleInputChange}
                        placeholder="Giá trị"
                        className="w-[100px]"
                      />

                      <Select
                        value={formData.budget_update_unit}
                        onValueChange={(value) => handleInputChange({ target: { name: 'budget_update_unit', value } })}
                      >
                        <SelectTrigger className="w-[80px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="%">%</SelectItem>
                          <SelectItem value="₫">₫</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label>Điều kiện</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCondition}
                    className="h-8"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Thêm điều kiện
                  </Button>
                </div>

                <div className="space-y-4">
                  {formData.conditions?.map((condition, index) => (
                    <div key={index} className="flex flex-col gap-2">
                      <div className="flex gap-2 items-start">
                        <div className="w-[180px]">
                          <Select className="w-[180px]"
                            value={condition.metric_type}
                            onValueChange={(value) => handleConditionChange(index, 'metric_type', value)}
                          >
                            <SelectTrigger className="w-[180px]">
                              <SelectValue placeholder="Chọn metric" />
                            </SelectTrigger>
                            <SelectContent className="w-[180px]">
                              {formData.action === 'Active Campaign' ? (
                                // Chỉ hiển thị Conv, CPA và Time khi chọn Active Campaign
                                metricTypeForActiveCampaign?.map(type => (
                                  <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                  </SelectItem>
                                ))
                              ) : (
                                // Hiển thị tất cả các metric types cho các action khác
                                metricTypes?.map(type => (
                                  <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="w-full flex gap-2">

                          {condition.metric_type === 'custom' ? (
                            <Input className="w-[400px]"
                              value={condition.value}
                              onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                              placeholder="Nhập công thức"
                            />
                          ) : condition.metric_type === 'time' ? (
                            <>
                              <Input className="w-[90px]"
                                type="time"
                                value={condition.value?.start || ''}
                                onChange={(e) => {
                                  const newValue = {
                                    ...condition.value,
                                    start: e.target.value
                                  };
                                  handleConditionChange(index, 'value', newValue);
                                }}
                                required
                              />
                              <span className="flex items-center">đến</span>
                              <Input className="w-[90px]"
                                type="time"
                                value={condition.value?.end || ''}
                                onChange={(e) => {
                                  const newValue = {
                                    ...condition.value,
                                    end: e.target.value
                                  };
                                  handleConditionChange(index, 'value', newValue);
                                }}
                                required
                              />
                            </>
                          ) : (<>
                            <Select
                              className="w-[300px]"
                              value={condition.comparison_operator}
                              onValueChange={(value) => handleConditionChange(index, 'comparison_operator', value)}
                            >
                              <SelectTrigger
                                className="w-[300px]">
                                <SelectValue placeholder="Chọn điều kiện" />
                              </SelectTrigger>
                              <SelectContent
                                className="w-[300px]">
                                {comparisonOperators?.map(op => (
                                  <SelectItem key={op.value} value={op.value}>
                                    {op.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Input
                              type="number"
                              value={condition.value}
                              onChange={(e) => handleConditionChange(index, 'value', e.target.value)}
                              placeholder="Giá trị"
                              className="w-[200px]"
                            />
                          </>
                          )}


                          {index < formData.conditions.length - 1 && (
                            <Select
                              value={condition.logical_operator}
                              onValueChange={(value) => handleConditionChange(index, 'logical_operator', value)}
                            >
                              <SelectTrigger className="w-[120px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {logicalOperators?.map(op => (
                                  <SelectItem key={op.value} value={op.value}>
                                    {op.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                          )}

                          {formData.conditions.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeCondition(index)}
                              className="h-8 w-8"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                >
                  Hủy
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Đang lưu...' : 'Lưu'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="rounded-md border overflow-x-auto bg-white">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow className="text-[12px]">
              <TableHead className="w-[120px] text-[11px]">Tên</TableHead>
              <TableHead className="w-[100px] text-[11px]">Tài khoản</TableHead>
              <TableHead className="w-[200px] text-[11px]">Điều kiện</TableHead>
              <TableHead className="w-[150px] text-[11px]">Hành động</TableHead>
              <TableHead className="w-[100px] text-center text-[11px]">Trạng thái</TableHead>
              <TableHead className="w-[150px] text-[11px]">Ngày tạo</TableHead>
              <TableHead className="w-[100px] text-center text-[11px]">Thao tác</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rules
              ?.filter(rule => !selectedAccount || rule.account_id === selectedAccount)
              ?.map((rule) => (
              <TableRow key={rule.id} className="text-[12px]">
                <TableCell className="w-[120px] bg-white text-[12px]">{rule.name}</TableCell>
                <TableCell className="w-[100px] bg-white text-[12px]">
                  {accounts?.find(account => account.customer_id == rule.account_id)?.name}
                </TableCell>
                <TableCell className="w-[200px] bg-white text-[12px]">
                  {rule.rule_conditions?.map((condition, index) => (
                    <div key={index}>
                      {condition.metric_type === 'custom' ? (
                        condition.value
                      ) : condition.metric_type === 'time' ? (
                        `Thời gian từ ${condition.value?.start} đến ${condition.value?.end}`
                      ) : (
                        <>
                          {metricTypes?.find(t => t.value === condition.metric_type)?.label} {' '}
                          {comparisonOperators?.find(op => op.value === condition.comparison_operator)?.label} {' '}
                          {condition.value}
                          {condition.metric_type === 'cost' && condition.comparison_operator === 'less_than_or_equals' && '₫'}
                          {condition.metric_type === 'cost' && condition.comparison_operator === 'greater_than_or_equals' && '₫'}
                          {condition.metric_type === 'cost' && condition.comparison_operator === 'less_than_or_equals_percent_budget' && '%'}
                          {condition.metric_type === 'cost' && condition.comparison_operator === 'greater_than_or_equals_percent_budget' && '%'}
                          {condition.metric_type === 'ctr' && '%'}
                          {condition.metric_type === 'cpa' && '₫'}
                          {condition.metric_type === 'cpc' && '₫'}
                          {condition.metric_type === 'cost_after_conv' && '₫'}
                          {condition.metric_type === 'impression' && ''}
                          {condition.metric_type === 'clicks' && ''}
                          {condition.metric_type === 'conversion_rate' && '%'}
                        </>
                      )}
                      {index < rule.rule_conditions?.length - 1 && ' '}
                      {condition.logical_operator === 'and' ? ' và ' : (condition.logical_operator === 'or' ? ' hoặc ' : '')}
                    </div>
                  ))}

                  {rule.action === 'Active Campaign' && rule.metric_time == '3d' && '(Dữ liệu dựa trên 3 ngày)'}
                  {rule.action === 'Active Campaign' && rule.metric_time == '7d' && '(Dữ liệu dựa trên 7 ngày)'}

                </TableCell>
                <TableCell className="w-[150px] bg-white text-[12px]">
                  {rule.action === 'Update Budget'
                    ? `Update Budget ${rule.budget_update_type} ${rule.budget_update_value}${rule.budget_update_unit}`
                    : rule.action}
                </TableCell>
                <TableCell className="w-[100px] bg-white text-center text-[12px]">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleStatusChange(rule.id, rule)}
                    className={rule.status === 'enable'
                      ? 'h-6 w-6 bg-green-100 text-green-800 hover:bg-green-200'
                      : 'h-6 w-6 bg-red-100 text-red-800 hover:bg-red-200'}
                  >
                    {rule.status === 'enable' ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                  </Button>
                </TableCell>
                <TableCell className="w-[150px] bg-white text-[12px]">
                  {new Date(rule.created_at).toLocaleString()}
                </TableCell>
                <TableCell className="w-[100px] bg-white text-center text-[12px]">
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleCopy(rule)}
                      className="h-6 w-6 bg-gray-200"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(rule)}
                      className="h-6 w-6 bg-gray-200"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(rule.id)}
                      className="h-6 w-6 bg-gray-200"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default RuleManager; 